<script setup lang="ts">
import type { Theme } from '../../theme-builder';
import { v4 as uuid } from 'uuid';
import { provide, computed } from 'vue';
import { kebabCase, each } from 'lodash';
import { useRoute } from 'vue-router';
import { cloudLight } from '../../theme-builder/themes';

const route = useRoute();

const props = defineProps<{
  theme: Theme;
}>();

const activeTheme = computed(() => {
  if ((route.fullPath as string).startsWith('/website')) {
    return cloudLight;
  }

  return props.theme;
});

provide('theme', activeTheme);

const themeScope = `theme-${uuid()}`;
const fontProperties = ['text.font', 'heading.font'];

const fonts = computed(() => {
  return fontProperties.map((fontProperty) => {
    return {
      family: activeTheme.value.get(`${fontProperty}.family`),
      weight: activeTheme.value.get(`${fontProperty}.weight`),
    };
  });
});

const style = computed(() => {
  let css = '\n';

  each(activeTheme.value.all(), (value, key) => {
    css += `  --tml-${kebabCase(key)}: ${value};\n`;
  });

  const pageBackground = activeTheme.value.get('page.background.color');
  const colorScheme = activeTheme.value.get('color-scheme');

  return `.${themeScope} {${css}}\nhtml, body {\n  background-color: ${pageBackground};\n  color-scheme: ${colorScheme};\n}`;
});
</script>

<template>
  <div
    class="app-theme"
    :class="{
      [themeScope]: true,
    }"
  >
    <slot />
    <Teleport to="head">
      <link
        v-for="font in fonts"
        :key="`${font.family}:${font.weight}`"
        :href="`https://fonts.googleapis.com/css2?family=${font.family}:wght@${font.weight}&display=swap`"
        rel="stylesheet"
      />
      <!-- eslint-disable-next-line -->
      <Component :is="'style'" v-html="style" />
    </Teleport>
  </div>
</template>

<style>
.app-theme {
  @apply bg-[--tml-page-background-color] text-[--tml-text-color];
  color-scheme: var(--tml-color-scheme);
  font-family: var(--tml-text-font-family), sans-serif;
  font-weight: var(--tml-text-font-weight), sans-serif;
}

.app-theme h1,
.app-theme h2,
.app-theme h3,
.app-theme h4,
.app-theme h5,
.app-theme h6 {
  font-family: var(--tml-heading-font-family), sans-serif;
  font-weight: var(--tml-heading-font-weight), sans-serif;
  /* text-transform: capitalize; */
}

.app-theme hr {
  border-color: var(--tml-card-border-color);
}
</style>
